import { useEffect, useRef, useState } from 'react';
import { useDsTranslate, useDsServices, useDsConfig } from '../context/DsProvider';
import styles from './OnlyOfficeEditor.module.scss';

// ── OnlyOffice API type declarations ─────────────────────────────────

interface OnlyOfficeDocEditor {
  destroyEditor: () => void;
}

interface DocsAPIGlobal {
  DocEditor: new (id: string, config: Record<string, unknown>) => OnlyOfficeDocEditor;
}

declare global {
  interface Window {
    DocsAPI?: DocsAPIGlobal;
  }
}

// ── Props ────────────────────────────────────────────────────────────

interface OnlyOfficeEditorProps {
  fileId: string;
  mode?: 'edit' | 'review' | 'review-author' | 'view';
  /**
   * Subject-type discriminator passed to `/v1/onlyoffice/editor-config?flow=…`.
   * Defaults to `'TEMPLATE'`; envelope-scoped surfaces must pass `'ENVELOPE'`
   * so the backend resolves the fileId against `envelope_document` rather than
   * `document_template`. Ignored when `configOverride` is supplied.
   */
  flow?: 'TEMPLATE' | 'ENVELOPE';
  /** Pre-fetched OnlyOffice config. When provided, skips the internal editorService.getEditorConfig call. */
  configOverride?: Record<string, unknown>;
  onSave?: () => void;
  onReady?: () => void;
  onError?: (error: string) => void;
  onDocKey?: (key: string) => void;
}

// ── Script loader (with onerror) ─────────────────────────────────────

let apiScriptLoaded = false;
let apiScriptLoading = false;
let apiScriptError: Error | null = null;
let currentApiScriptUrl = '';
const apiScriptCallbacks: { resolve: () => void; reject: (err: Error) => void }[] = [];

function loadApiScript(apiScriptUrl: string): Promise<void> {
  if (apiScriptLoaded && currentApiScriptUrl === apiScriptUrl) return Promise.resolve();
  if (apiScriptError) return Promise.reject(apiScriptError);
  return new Promise((resolve, reject) => {
    if (apiScriptLoading) {
      apiScriptCallbacks.push({ resolve, reject });
      return;
    }
    apiScriptLoading = true;
    currentApiScriptUrl = apiScriptUrl;
    const script = document.createElement('script');
    script.src = apiScriptUrl;
    script.async = true;
    script.onload = () => {
      apiScriptLoaded = true;
      apiScriptLoading = false;
      resolve();
      apiScriptCallbacks.forEach((cb) => cb.resolve());
      apiScriptCallbacks.length = 0;
    };
    script.onerror = () => {
      apiScriptLoading = false;
      apiScriptError = new Error(`Failed to load OnlyOffice script: ${apiScriptUrl}`);
      reject(apiScriptError);
      apiScriptCallbacks.forEach((cb) => cb.reject(apiScriptError!));
      apiScriptCallbacks.length = 0;
    };
    document.head.appendChild(script);
  });
}

let editorCounter = 0;

// ── Component ────────────────────────────────────────────────────────

export function OnlyOfficeEditor({
  fileId,
  mode = 'edit',
  flow = 'TEMPLATE',
  configOverride,
  onSave,
  onReady,
  onError,
  onDocKey,
}: OnlyOfficeEditorProps) {
  const t = useDsTranslate();
  const { editor: editorService } = useDsServices();
  const { onlyOfficeUrl } = useDsConfig();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<OnlyOfficeDocEditor | null>(null);
  const editorIdRef = useRef(`oo-editor-${++editorCounter}`);

  const onSaveRef = useRef(onSave);
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onDocKeyRef = useRef(onDocKey);
  onSaveRef.current = onSave;
  onReadyRef.current = onReady;
  onErrorRef.current = onError;
  onDocKeyRef.current = onDocKey;

  useEffect(() => {
    let destroyed = false;
    const currentEditorId = editorIdRef.current;
    const apiScriptUrl = `${onlyOfficeUrl}/web-apps/apps/api/documents/api.js`;

    async function init() {
      try {
        if (!configOverride && !editorService) {
          throw new Error('Editor service not provided to DsProvider');
        }

        setLoading(true);
        setError(null);

        await loadApiScript(apiScriptUrl);
        if (destroyed) return;

        const config = configOverride ?? await editorService!.getEditorConfig(fileId, mode, flow);
        if (destroyed) return;

        // `configOverride` is typed loose (Record<string, unknown>) so that
        // app-layer services can return raw JSON; narrow to extract docKey.
        const docKey = (config as { document?: { key?: string } }).document?.key;
        if (docKey) {
          onDocKeyRef.current?.(docKey);
        }

        if (!containerRef.current) return;
        containerRef.current.innerHTML = '';

        const editorDiv = document.createElement('div');
        editorDiv.id = currentEditorId;
        editorDiv.style.width = '100%';
        editorDiv.style.height = '100%';
        containerRef.current.appendChild(editorDiv);

        const DocsAPI = window.DocsAPI;
        if (!DocsAPI) {
          throw new Error('OnlyOffice API not loaded');
        }

        editorRef.current = new DocsAPI.DocEditor(currentEditorId, {
          ...config,
          height: '100%',
          width: '100%',
          events: {
            onDocumentReady: () => {
              // Inject CSS to hide OnlyOffice branding — validate iframe src first
              try {
                const iframe = containerRef.current?.querySelector('iframe');
                if (iframe) {
                  const iframeSrc = iframe.getAttribute('src') ?? '';
                  if (iframeSrc.startsWith(onlyOfficeUrl) && iframe.contentDocument) {
                    const style = iframe.contentDocument.createElement('style');
                    style.textContent = `
                      #header-logo, .brand-logo, .toolbar-group-left .ribtab:first-child,
                      [data-tab="home"]::before, .about-logo, .logo-img,
                      .asc-window-about .logo,
                      li[data-tab="file"], li[data-tab="protect"],
                      li[data-tab="plugins"], li[data-tab="ai"],
                      #btn-doc-lang, #btn-cnt-words, .cnt-lang, .cnt-words,
                      .statusbar .el-tab-word-count, .statusbar .el-tab-language
                      { display: none !important; }
                    `;
                    iframe.contentDocument.head.appendChild(style);
                  }
                }
              } catch { /* ignore cross-origin errors */ }
              onReadyRef.current?.();
            },
            onDocumentStateChange: (event: { data: boolean }) => {
              if (event.data === false) {
                onSaveRef.current?.();
              }
            },
            onError: (event: { data: { errorCode: number; errorDescription: string } }) => {
              onErrorRef.current?.(event.data?.errorDescription || 'Editor error');
            },
          },
        });

        setLoading(false);
      } catch (err: unknown) {
        if (!destroyed) {
          const msg = err instanceof Error ? err.message : 'Failed to load editor';
          setError(msg);
          setLoading(false);
          onErrorRef.current?.(msg);
        }
      }
    }

    init();

    return () => {
      destroyed = true;
      if (editorRef.current) {
        try {
          editorRef.current.destroyEditor();
        } catch { /* ignore */ }
        editorRef.current = null;
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [fileId, mode, flow, configOverride, editorService, onlyOfficeUrl]);

  return (
    <div className={styles.container}>
      {loading && <div className={styles.loading}>{t('K_DS_LOADING_EDITOR', 'Loading editor...')}</div>}
      {error && (
        <div className={styles.error}>
          <p>{t('K_DS_EDITOR_LOAD_FAILED', 'Failed to load editor')}</p>
          <p className={styles.errorDetail}>{error}</p>
        </div>
      )}
      <div
        ref={containerRef}
        className={styles.editorHost}
        style={{ display: loading || error ? 'none' : 'block' }}
      />
    </div>
  );
}
